import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Sparkles, Play, Video, MessageCircle, Users, Calendar, Clock, Loader2, Zap, Heart, Shield, Hand, FileText, Send, Lock, BookOpen, FlaskConical, BarChart3, ChevronRight, Star, Trophy, GraduationCap, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

function AlignmentLabPage() {
  const { getAuthHeader, user } = useAuth();
  const [searchParams] = useSearchParams();
  const [hasAccess, setHasAccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState(null);
  const [activeWeek, setActiveWeek] = useState(1);
  const [activeSection, setActiveSection] = useState('start'); // 'start', 'week', or 'alumni'
  const [purchasing, setPurchasing] = useState(false);
  const [threads, setThreads] = useState([]);
  const [startHereThreads, setStartHereThreads] = useState([]);
  const [showThreadForm, setShowThreadForm] = useState(false);
  const [newThreadContent, setNewThreadContent] = useState('');
  const [mondayResponse, setMondayResponse] = useState('');
  const [wednesdayComment, setWednesdayComment] = useState('');
  const [fridayReflection, setFridayReflection] = useState('');
  const [posting, setPosting] = useState(false);
  const [unlockedWeeks, setUnlockedWeeks] = useState(4);
  const [daysSincePurchase, setDaysSincePurchase] = useState(0);
  const [activeStartThread, setActiveStartThread] = useState(null);
  const [alumniLoungeAccess, setAlumniLoungeAccess] = useState(false);
  const [alumniContent, setAlumniContent] = useState(null);
  const [alumniThreads, setAlumniThreads] = useState([]);
  const [activeAlumniThread, setActiveAlumniThread] = useState(null);

  // Calculate which day of the week we're on for the current unlocked week (0=Mon, 1=Tue, 2=Wed, 3=Thu, 4=Fri, 5=Sat, 6=Sun)
  const getDayOfCurrentWeek = function() {
    // daysSincePurchase tells us how many days since the wave started
    // Week 1 = days 0-6, Week 2 = days 7-13, etc.
    const daysIntoCurrentWeek = daysSincePurchase % 7;
    return daysIntoCurrentWeek;
  };

  // Check if a week is fully completed (past the 7 days of that week)
  const isWeekFullyCompleted = function(weekNum) {
    // Week 1 complete at day 7, Week 2 at day 14, Week 3 at day 21, Week 4 at day 28
    const daysNeededToComplete = weekNum * 7;
    return daysSincePurchase >= daysNeededToComplete;
  };

  // Check if Wednesday content should be visible for a given week
  const isWednesdayUnlocked = function(weekNum) {
    // If this week is fully completed, show all content
    if (isWeekFullyCompleted(weekNum)) return true;
    // If this week is before the current unlocked week, show all content
    if (weekNum < unlockedWeeks) return true;
    // If this week is after the current unlocked week, it's fully locked
    if (weekNum > unlockedWeeks) return false;
    // This is the current week - check if it's Wednesday (day 2) or later
    return getDayOfCurrentWeek() >= 2;
  };

  // Check if Friday content should be visible for a given week
  const isFridayUnlocked = function(weekNum) {
    // If this week is fully completed, show all content
    if (isWeekFullyCompleted(weekNum)) return true;
    // If this week is before the current unlocked week, show all content
    if (weekNum < unlockedWeeks) return true;
    // If this week is after the current unlocked week, it's fully locked
    if (weekNum > unlockedWeeks) return false;
    // This is the current week - check if it's Friday (day 4) or later
    return getDayOfCurrentWeek() >= 4;
  };

  useEffect(function() {
    async function checkAccess() {
      try {
        const response = await axios.get(API_URL + '/alignment-lab/access', { headers: getAuthHeader() });
        setHasAccess(response.data.has_access);
        setUnlockedWeeks(response.data.unlocked_weeks || 4);
        setDaysSincePurchase(response.data.days_since_wave_start || 0);
        setAlumniLoungeAccess(response.data.alumni_lounge_access || false);
      } catch (error) {
        console.error('Failed to check access:', error);
      } finally {
        setLoading(false);
      }
    }
    
    async function verifyPayment(sessionId) {
      try {
        const response = await axios.get(API_URL + '/alignment-lab/payment-status/' + sessionId, { headers: getAuthHeader() });
        if (response.data.status === 'paid') {
          toast.success('Welcome to the Alignment Lab!');
          setHasAccess(true);
          setUnlockedWeeks(1);
          setActiveWeek(1);
          window.history.replaceState({}, '', '/dashboard/alignment-lab');
        }
      } catch (error) {
        console.error('Payment verification failed:', error);
      }
    }
    
    checkAccess();
    const sessionId = searchParams.get('session_id');
    if (sessionId) verifyPayment(sessionId);
  }, [getAuthHeader, searchParams]);

  useEffect(function() {
    if (!hasAccess) return;
    
    async function fetchContent() {
      try {
        const response = await axios.get(API_URL + '/alignment-lab/content', { headers: getAuthHeader() });
        setContent(response.data);
      } catch (error) {
        console.error('Failed to fetch content:', error);
      }
    }
    
    async function fetchStartHereThreads() {
      try {
        const response = await axios.get(API_URL + '/alignment-lab/start-here', { headers: getAuthHeader() });
        setStartHereThreads(response.data);
      } catch (error) {
        console.error('Failed to fetch start here threads:', error);
      }
    }
    
    async function fetchAlumniContent() {
      if (!alumniLoungeAccess) return;
      try {
        const response = await axios.get(API_URL + '/alignment-lab/alumni-lounge', { headers: getAuthHeader() });
        setAlumniContent(response.data);
      } catch (error) {
        console.error('Failed to fetch alumni content:', error);
      }
    }
    
    fetchContent();
    fetchStartHereThreads();
    fetchAlumniContent();
  }, [hasAccess, alumniLoungeAccess, getAuthHeader]);

  useEffect(function() {
    if (!hasAccess || activeSection === 'start') return;
    
    async function fetchThreads() {
      try {
        const response = await axios.get(API_URL + '/community/threads/alignment-lab?week=' + activeWeek, { headers: getAuthHeader() });
        setThreads(response.data);
      } catch (error) {
        console.error('Failed to fetch threads:', error);
      }
    }
    
    if (activeSection === 'week' && activeWeek <= unlockedWeeks) {
      fetchThreads();
    }
  }, [hasAccess, activeWeek, activeSection, getAuthHeader, unlockedWeeks]);

  useEffect(function() {
    if (!hasAccess || activeSection !== 'alumni' || !alumniLoungeAccess) return;
    
    async function fetchAlumniThreads() {
      try {
        const response = await axios.get(API_URL + '/alignment-lab/alumni-lounge/threads', { headers: getAuthHeader() });
        setAlumniThreads(response.data);
      } catch (error) {
        console.error('Failed to fetch alumni threads:', error);
      }
    }
    
    fetchAlumniThreads();
  }, [hasAccess, activeSection, alumniLoungeAccess, getAuthHeader]);

  async function handlePurchase() {
    setPurchasing(true);
    try {
      const response = await axios.post(API_URL + '/alignment-lab/checkout', { origin_url: window.location.origin }, { headers: getAuthHeader() });
      if (response.data.url) {
        const newTab = window.open(response.data.url, '_blank');
        if (!newTab || newTab.closed) {
          // Pop-up blocked — fall back to same-tab redirect
          window.location.href = response.data.url;
          return;
        }
        toast.success('Payment page opened in a new tab');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
    }
    setPurchasing(false);
  }

  async function handlePostThread() {
    if (!newThreadContent.trim()) return;
    try {
      var weekContent = content && content.weeks ? content.weeks.find(function(w) { return w.week === activeWeek; }) : null;
      await axios.post(API_URL + '/community/threads/alignment-lab', {
        week: activeWeek,
        thread_type: 'friday',
        title: weekContent && weekContent.friday_thread ? weekContent.friday_thread.title : 'Week ' + activeWeek + ' Thread',
        content: newThreadContent
      }, { headers: getAuthHeader() });
      toast.success('Posted!');
      setNewThreadContent('');
      setShowThreadForm(false);
      const response = await axios.get(API_URL + '/community/threads/alignment-lab?week=' + activeWeek, { headers: getAuthHeader() });
      setThreads(response.data);
    } catch (error) {
      toast.error('Failed to post');
    }
  }

  async function handlePostWeeklyThread(threadType, content, setter) {
    if (!content.trim()) return;
    setPosting(true);
    try {
      var weekContent = activeWeekContent;
      var title = 'Week ' + activeWeek;
      if (threadType === 'monday' && weekContent && weekContent.monday_experiment) {
        title = weekContent.monday_experiment.title || title;
      } else if (threadType === 'wednesday' && weekContent && weekContent.wednesday_checkin) {
        title = weekContent.wednesday_checkin.title || title;
      } else if (threadType === 'friday' && weekContent && weekContent.friday_thread) {
        title = weekContent.friday_thread.title || title;
      }
      await axios.post(API_URL + '/community/threads/alignment-lab', {
        week: activeWeek,
        thread_type: threadType,
        title: title,
        content: content
      }, { headers: getAuthHeader() });
      toast.success('Posted!');
      setter('');
      var response = await axios.get(API_URL + '/community/threads/alignment-lab?week=' + activeWeek, { headers: getAuthHeader() });
      setThreads(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post');
    } finally {
      setPosting(false);
    }
  }

  var activeWeekContent = content && content.weeks ? content.weeks.find(function(w) { return w.week === activeWeek; }) : null;

  async function handleDeleteThread(threadId) {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    try {
      await axios.delete(API_URL + '/community/threads/alignment-lab/' + threadId, { headers: getAuthHeader() });
      toast.success('Post deleted');
      setThreads(threads.filter(function(t) { return t.id !== threadId; }));
      setStartHereThreads(startHereThreads.filter(function(t) { return t.id !== threadId; }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  }

  async function handleDeleteReply(threadId, replyId) {
    if (!window.confirm('Are you sure you want to delete this reply?')) return;
    try {
      await axios.delete(API_URL + '/community/threads/alignment-lab/' + threadId + '/reply/' + replyId, { headers: getAuthHeader() });
      toast.success('Reply deleted');
      // Refresh both weekly and start-here threads
      var weekResponse = await axios.get(API_URL + '/community/threads/alignment-lab?week=' + activeWeek, { headers: getAuthHeader() });
      setThreads(weekResponse.data);
      var startResponse = await axios.get(API_URL + '/alignment-lab/start-here', { headers: getAuthHeader() });
      setStartHereThreads(startResponse.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  }

  function getDaysUntilUnlock(week) {
    var daysNeeded = (week - 1) * 7;
    return Math.max(0, daysNeeded - daysSincePurchase);
  }

  function selectWeek(week) {
    setActiveWeek(week);
    setActiveSection('week');
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Sales page for non-members
  if (!hasAccess) {
    return (
      <div className="space-y-8" data-testid="alignment-lab-sales">
        <div className="card-cosmic p-8 bg-gradient-to-br from-violet-500/10 via-fuchsia-500/10 to-amber-500/10 text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
            <Zap className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-stone-900 mb-4">30-Day Alignment Lab</h1>
          <p className="text-lg text-stone-600 max-w-2xl mx-auto mb-4">
            A 4-week guided realignment for women ready to choose themselves without guilt
          </p>
          <p className="text-base text-stone-500 max-w-2xl mx-auto mb-6">
            Practice rest, stronger boundaries, and deeper self-trust through small weekly experiments, live support, and a community that gets it.
          </p>
          
          {/* Launch Date Announcement */}
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white px-4 py-2 rounded-full text-sm font-semibold mb-4">
            <Calendar className="w-4 h-4" />
            Starts April 20, 2026
          </div>
          
          <div className="text-3xl font-bold text-primary mb-2">$222</div>
          <p className="text-stone-500 mb-6">One-time payment · Lifetime access</p>
          <Button onClick={handlePurchase} disabled={purchasing} className="btn-cosmic text-lg px-8 py-6" data-testid="purchase-alignment-lab-btn">
            {purchasing ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Sparkles className="w-5 h-5 mr-2" />}
            Join the Alignment Lab
          </Button>
        </div>

        {/* This is for you if... */}
        <div className="card-cosmic p-8">
          <h2 className="text-2xl font-bold text-stone-900 mb-6 text-center">This is for you if...</h2>
          <ul className="space-y-4 max-w-2xl mx-auto">
            <li className="flex items-start gap-3">
              <span className="mt-1.5 w-2 h-2 rounded-full bg-violet-500 flex-shrink-0" />
              <span className="text-stone-700">You are good at taking care of everything except yourself</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-1.5 w-2 h-2 rounded-full bg-violet-500 flex-shrink-0" />
              <span className="text-stone-700">You look capable on the outside but feel tired, stretched, or disconnected on the inside</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-1.5 w-2 h-2 rounded-full bg-violet-500 flex-shrink-0" />
              <span className="text-stone-700">You keep telling yourself you'll slow down, set boundaries, or choose yourself later</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-1.5 w-2 h-2 rounded-full bg-violet-500 flex-shrink-0" />
              <span className="text-stone-700">Guilt shows up the moment you try to make space for your own needs</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-1.5 w-2 h-2 rounded-full bg-violet-500 flex-shrink-0" />
              <span className="text-stone-700">You are ready for change, but not through pressure, perfectionism, or pretending</span>
            </li>
          </ul>
        </div>

        <div className="card-cosmic p-8">
          <div className="max-w-2xl mx-auto space-y-4 text-stone-700 text-center">
            <p className="text-xl font-semibold text-stone-900">You are not broken. You may simply be out of alignment.</p>
            <p>Maybe you have been holding a lot for a long time.</p>
            <p>Maybe you are capable, caring, responsible, and deeply tired.</p>
            <p>Maybe you keep telling yourself that once things calm down, you'll finally make space for yourself.</p>
            <p className="font-medium text-stone-800">But later keeps becoming later.</p>
            <p>The Alignment Lab is a place to interrupt that pattern.</p>
            <p>Not through force.</p>
            <p>Not through fixing yourself.</p>
            <p>Through honest awareness, small experiments, supportive reflection, and the practice of choosing yourself in ways that actually fit real life.</p>
          </div>
        </div>

        <div className="card-cosmic p-8">
          <div className="max-w-2xl mx-auto space-y-4 text-stone-700 text-center">
            <p>This is not another challenge to perform your way through.</p>
            <p>This is not about becoming more disciplined, getting your life perfect, or finally forcing yourself into better habits.</p>
            <p className="font-medium text-stone-800">This is about practicing alignment in real life.</p>
            <p>Inside the Lab, you'll work with small experiments, supportive reflection, and honest shifts that help you create more space, more clarity, and more trust in yourself.</p>
          </div>
        </div>

        <div className="card-cosmic p-8">
          <h2 className="text-2xl font-bold text-stone-900 mb-6 text-center">Your Weekly Rhythm</h2>
          <p className="text-center text-stone-600 mb-6">Starting Monday, April 20th • First check-in Wednesday, April 22nd</p>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center p-6 bg-violet-50 rounded-2xl">
              <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-violet-500 flex items-center justify-center">
                <Video className="w-7 h-7 text-white" />
              </div>
              <h3 className="font-bold text-stone-900 mb-2">Monday</h3>
              <p className="text-stone-600">Alignment Experiment</p>
            </div>
            <div className="text-center p-6 bg-fuchsia-50 rounded-2xl">
              <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-fuchsia-500 flex items-center justify-center">
                <Users className="w-7 h-7 text-white" />
              </div>
              <h3 className="font-bold text-stone-900 mb-2">Wednesday</h3>
              <p className="text-stone-600">Live Group Check-in (6 PM ET)</p>
            </div>
            <div className="text-center p-6 bg-amber-50 rounded-2xl">
              <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-amber-500 flex items-center justify-center">
                <MessageCircle className="w-7 h-7 text-white" />
              </div>
              <h3 className="font-bold text-stone-900 mb-2">Friday</h3>
              <p className="text-stone-600">Community Thread</p>
            </div>
          </div>
        </div>

        {/* Alumni Lounge Highlight */}
        <div className="card-cosmic p-6 bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center flex-shrink-0">
              <GraduationCap className="w-7 h-7 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-stone-900 text-lg mb-2">Lifetime Access + Alumni Lounge</h3>
              <p className="text-stone-600">
                Your Lab includes lifetime access, plus an Alumni Lounge that opens after you complete 
                the 4-week path—so you can stay connected, keep practicing, and be held by women who truly get it.
              </p>
            </div>
          </div>
        </div>

        <div className="card-cosmic p-8">
          <h2 className="text-2xl font-bold text-stone-900 mb-6 text-center">Your 4-Week Journey</h2>
          <p className="text-center text-stone-600 max-w-2xl mx-auto mb-4">
            Your 4-week guided reset to help you practice rest, clearer boundaries, healthier availability, and the kind of no that brings you back to yourself.
          </p>
          <p className="text-center text-stone-500 mb-6">Content unlocks weekly as you progress through the lab</p>
          <div className="space-y-4">
            <div className="p-5 rounded-2xl bg-rose-50 border border-rose-100">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-rose-500 flex items-center justify-center">
                  <Heart className="w-6 h-6 text-white" />
                </div>
                <div>
                  <span className="text-sm text-stone-500">Week 1</span>
                  <h3 className="font-bold text-stone-900">Rest is Worth</h3>
                  <p className="text-stone-600 text-sm font-medium">Experiment: The 15-Minute First Yes</p>
                  <p className="text-stone-500 text-sm mt-1">Learn to stop treating rest like a reward and start relating to it as something you are worthy of now.</p>
                </div>
              </div>
            </div>
            <div className="p-5 rounded-2xl bg-blue-50 border border-blue-100">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-white" />
                </div>
                <div>
                  <span className="text-sm text-stone-500">Week 2</span>
                  <h3 className="font-bold text-stone-900">Time Boundaries That Hold</h3>
                  <p className="text-stone-600 text-sm font-medium">Experiment: The Time Fence</p>
                  <p className="text-stone-500 text-sm mt-1">Create realistic boundaries around your time so your energy stops leaking out everywhere.</p>
                </div>
              </div>
            </div>
            <div className="p-5 rounded-2xl bg-purple-50 border border-purple-100">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-purple-500 flex items-center justify-center">
                  <Shield className="w-6 h-6 text-white" />
                </div>
                <div>
                  <span className="text-sm text-stone-500">Week 3</span>
                  <h3 className="font-bold text-stone-900">Availability Is Not Love</h3>
                  <p className="text-stone-600 text-sm font-medium">Experiment: The Availability Reset</p>
                  <p className="text-stone-500 text-sm mt-1">Untangle your worth from always being reachable, responsive, and endlessly open.</p>
                </div>
              </div>
            </div>
            <div className="p-5 rounded-2xl bg-amber-50 border border-amber-100">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-amber-500 flex items-center justify-center">
                  <Hand className="w-6 h-6 text-white" />
                </div>
                <div>
                  <span className="text-sm text-stone-500">Week 4</span>
                  <h3 className="font-bold text-stone-900">The Clean No</h3>
                  <p className="text-stone-600 text-sm font-medium">Experiment: The Clean No</p>
                  <p className="text-stone-500 text-sm mt-1">Practice honest, grounded boundaries without over-explaining, guilt-spiraling, or self-betrayal.</p>
                </div>
              </div>
            </div>
            {/* Alumni Lounge */}
            <div className="p-5 rounded-2xl bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-200">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
                  <GraduationCap className="w-6 h-6 text-white" />
                </div>
                <div>
                  <span className="text-sm text-amber-600 font-medium">After Week 4 • Lifetime Access</span>
                  <h3 className="font-bold text-stone-900">Alumni Lounge</h3>
                  <p className="text-stone-600 text-sm">Stay connected, keep practicing, rejoin live waves anytime</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="card-cosmic p-8">
          <div className="max-w-2xl mx-auto text-center">
            <p className="text-stone-700 mb-4">By the end of these 30 days, you may not have your whole life figured out.</p>
            <p className="text-lg font-semibold text-stone-900 mb-4">But you will have something powerful:</p>
            <ul className="space-y-3 text-left max-w-md mx-auto">
              <li className="flex items-start gap-3">
                <span className="mt-1.5 w-2 h-2 rounded-full bg-violet-500 flex-shrink-0" />
                <span className="text-stone-700">More awareness of what drains and supports you</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-1.5 w-2 h-2 rounded-full bg-violet-500 flex-shrink-0" />
                <span className="text-stone-700">More clarity about where you've been abandoning yourself</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-1.5 w-2 h-2 rounded-full bg-violet-500 flex-shrink-0" />
                <span className="text-stone-700">Practical ways to choose yourself with less guilt</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-1.5 w-2 h-2 rounded-full bg-violet-500 flex-shrink-0" />
                <span className="text-stone-700">Stronger trust in your own knowing</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-1.5 w-2 h-2 rounded-full bg-violet-500 flex-shrink-0" />
                <span className="text-stone-700">Real momentum toward a life that feels more like yours</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="text-center">
          <Button onClick={handlePurchase} disabled={purchasing} className="btn-cosmic text-lg px-8 py-6">
            {purchasing ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Sparkles className="w-5 h-5 mr-2" />}
            Start Your Alignment Journey - $222
          </Button>
        </div>
      </div>
    );
  }

  // Member dashboard
  var currentWeek = content && content.weeks ? content.weeks.find(function(w) { return w.week === activeWeek; }) : null;
  var weekIcons = { 1: Heart, 2: Clock, 3: Shield, 4: Hand };
  var isWeekLocked = activeWeek > unlockedWeeks;
  var startHereInfo = content && content.start_here ? content.start_here : null;

  var startThreadIcons = {
    'start-agreements': BookOpen,
    'start-experiment': FlaskConical,
    'start-wins-wobbles': BarChart3
  };

  var alumniThreadIcons = {
    'alumni-worth-standard': Star,
    'alumni-scripts': MessageCircle,
    'alumni-receipts': Trophy
  };

  return (
    <div className="space-y-6" data-testid="alignment-lab-dashboard">
      <div className="flex items-center gap-4">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
          <Zap className="w-7 h-7 text-white" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-stone-900">30-Day Alignment Lab</h1>
          <p className="text-stone-600">Day {daysSincePurchase + 1} • Week {unlockedWeeks} unlocked</p>
        </div>
      </div>

      {/* Navigation: Start Here + Weeks */}
      <div className="flex flex-wrap gap-3">
        {/* Start Here Button */}
        <button 
          onClick={function() { setActiveSection('start'); }} 
          data-testid="start-here-btn"
          className={'px-5 py-3 rounded-xl text-left transition-all flex items-center gap-2 ' + 
            (activeSection === 'start' 
              ? 'bg-gradient-to-br from-emerald-500 to-teal-500 text-white shadow-lg' 
              : 'bg-white border border-stone-200 hover:border-emerald-300 text-stone-700'
            )}
        >
          <BookOpen className={'w-5 h-5 ' + (activeSection === 'start' ? 'text-white' : 'text-emerald-500')} />
          <span className="font-semibold">Start Here</span>
        </button>

        {/* Week Buttons */}
        {content && content.weeks && content.weeks.map(function(week) {
          var Icon = weekIcons[week.week] || Sparkles;
          var isActive = activeSection === 'week' && activeWeek === week.week;
          var isLocked = week.week > unlockedWeeks;
          var daysUntil = getDaysUntilUnlock(week.week);
          
          return (
            <button 
              key={week.week} 
              onClick={function() { selectWeek(week.week); }} 
              data-testid={'week-' + week.week + '-btn'}
              className={'px-4 py-3 rounded-xl text-left transition-all relative flex items-center gap-2 ' + 
                (isActive 
                  ? 'bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white shadow-lg' 
                  : isLocked 
                    ? 'bg-stone-100 border border-stone-200 opacity-75 text-stone-500'
                    : 'bg-white border border-stone-200 hover:border-violet-300 text-stone-700'
                )}
            >
              {isLocked && <Lock className="w-4 h-4" />}
              <Icon className={'w-5 h-5 ' + (isActive ? 'text-white' : isLocked ? 'text-stone-400' : 'text-violet-500')} />
              <div>
                <div className={'text-xs font-medium ' + (isActive ? 'text-white/80' : 'text-stone-500')}>
                  Week {week.week}{isLocked && ' • ' + daysUntil + 'd'}
                </div>
                <div className={'font-semibold text-sm ' + (isActive ? 'text-white' : '')}>{week.theme}</div>
              </div>
            </button>
          );
        })}

        {/* Alumni Lounge Button */}
        <button 
          onClick={function() { 
            if (alumniLoungeAccess) {
              setActiveSection('alumni'); 
            }
          }} 
          data-testid="alumni-lounge-btn"
          className={'px-5 py-3 rounded-xl text-left transition-all flex items-center gap-2 ' + 
            (activeSection === 'alumni' 
              ? 'bg-gradient-to-br from-amber-500 to-orange-500 text-white shadow-lg' 
              : alumniLoungeAccess
                ? 'bg-white border border-stone-200 hover:border-amber-300 text-stone-700'
                : 'bg-stone-100 border border-stone-200 opacity-60 text-stone-500 cursor-not-allowed'
            )}
        >
          {!alumniLoungeAccess && <Lock className="w-4 h-4" />}
          <GraduationCap className={'w-5 h-5 ' + (activeSection === 'alumni' ? 'text-white' : alumniLoungeAccess ? 'text-amber-500' : 'text-stone-400')} />
          <div>
            <div className={'text-xs font-medium ' + (activeSection === 'alumni' ? 'text-white/80' : 'text-stone-500')}>
              {alumniLoungeAccess ? 'Unlocked' : 'After Week 4'}
            </div>
            <div className={'font-semibold text-sm ' + (activeSection === 'alumni' ? 'text-white' : '')}>Alumni Lounge</div>
          </div>
        </button>
      </div>

      {/* Start Here Section */}
      {activeSection === 'start' && startHereInfo && (
        <div className="space-y-4">
          <div className="card-cosmic p-6">
            <h2 className="text-xl font-bold text-stone-900 mb-2">{startHereInfo.title}</h2>
            <p className="text-stone-600 mb-6">{startHereInfo.description}</p>

            <div className="space-y-3">
              {startHereInfo.threads && startHereInfo.threads.map(function(thread) {
                var Icon = startThreadIcons[thread.id] || BookOpen;
                var isActive = activeStartThread === thread.id;
                
                return (
                  <button
                    key={thread.id}
                    onClick={function() { setActiveStartThread(isActive ? null : thread.id); }}
                    className={'w-full p-4 rounded-xl text-left transition-all ' + 
                      (isActive 
                        ? 'bg-emerald-50 border-2 border-emerald-300' 
                        : 'bg-stone-50 border border-stone-200 hover:border-emerald-300'
                      )}
                  >
                    <div className="flex items-center gap-4">
                      <div className={'w-12 h-12 rounded-xl flex items-center justify-center ' + 
                        (isActive ? 'bg-emerald-500' : 'bg-emerald-100')}>
                        <Icon className={'w-6 h-6 ' + (isActive ? 'text-white' : 'text-emerald-600')} />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-stone-900">{thread.title}</h3>
                        <p className="text-sm text-stone-600">{thread.description}</p>
                      </div>
                      <ChevronRight className={'w-5 h-5 transition-transform ' + 
                        (isActive ? 'rotate-90 text-emerald-500' : 'text-stone-400')} />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Thread Content Area */}
          {activeStartThread && (
            <div className="card-cosmic p-6">
              <div className="flex items-center gap-3 mb-4">
                {(function() {
                  var Icon = startThreadIcons[activeStartThread] || BookOpen;
                  return <Icon className="w-6 h-6 text-emerald-500" />;
                })()}
                <h3 className="font-bold text-stone-900 text-lg">
                  {startHereInfo.threads.find(function(t) { return t.id === activeStartThread; })?.title}
                </h3>
              </div>
              
              {/* Display existing threads or placeholder */}
              {(function() {
                var threadInfo = startHereInfo.threads.find(function(t) { return t.id === activeStartThread; });
                var existingThreads = startHereThreads.filter(function(t) { return t.start_thread_id === activeStartThread; });
                
                return (
                  <div className="space-y-6">
                    {/* Show detailed content if available */}
                    {threadInfo && threadInfo.detailed_content && (
                      <div className="p-6 bg-emerald-50 border-2 border-emerald-200 rounded-xl">
                        <div className="prose prose-stone max-w-none">
                          <div className="text-stone-700 whitespace-pre-wrap" dangerouslySetInnerHTML={{
                            __html: threadInfo.detailed_content
                              .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
                              .replace(/^• /gm, '&bull; ')
                              .replace(/\n/g, '<br/>')
                          }} />
                        </div>
                      </div>
                    )}
                    
                    {/* Show existing threads from database */}
                    {existingThreads.length > 0 && existingThreads.map(function(thread) {
                    return (
                      <div key={thread.id}>
                        {/* Post */}
                        <div className={'p-6 rounded-xl ' + (thread.is_pinned ? 'bg-emerald-50 border-2 border-emerald-200' : 'bg-stone-50')}>
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
                              <span className="text-white font-medium">H</span>
                            </div>
                            <div>
                              <p className="font-medium text-stone-900">{thread.author_name}</p>
                              <p className="text-xs text-stone-500">{new Date(thread.created_at).toLocaleDateString()}</p>
                            </div>
                            {(thread.author_id === user?.id || user?.is_admin) && (
                              <button data-testid={'delete-start-thread-' + thread.id} onClick={function() { handleDeleteThread(thread.id); }} className="ml-auto p-1 hover:bg-red-50 rounded text-stone-400 hover:text-red-500 transition-colors" title="Delete">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                          <div className="prose prose-stone max-w-none">
                            <div className="text-stone-700 whitespace-pre-wrap" dangerouslySetInnerHTML={{
                              __html: thread.content
                                .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
                                .replace(/^• /gm, '&bull; ')
                            }} />
                          </div>
                        </div>

                        {/* Replies Section */}
                        {thread.replies && thread.replies.length > 0 && (
                          <div className="mt-4 space-y-3">
                            <p className="text-sm font-medium text-stone-500 px-2">{thread.replies.length} Introduction{thread.replies.length !== 1 ? 's' : ''}</p>
                            {thread.replies.map(function(reply) {
                              return (
                                <div key={reply.id} className="p-4 bg-white border border-stone-200 rounded-xl ml-4">
                                  <div className="flex items-center gap-3 mb-2">
                                    {reply.author_avatar ? (
                                      <img src={reply.author_avatar} alt="" className="w-8 h-8 rounded-full object-cover" />
                                    ) : (
                                      <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center">
                                        <span className="text-violet-600 font-medium text-sm">{reply.author_name ? reply.author_name.charAt(0).toUpperCase() : '?'}</span>
                                      </div>
                                    )}
                                    <div>
                                      <p className="font-medium text-stone-900 text-sm">{reply.author_name}</p>
                                      <p className="text-xs text-stone-500">{new Date(reply.created_at).toLocaleDateString()}</p>
                                    </div>
                                    {(reply.author_id === user?.id || user?.is_admin) && (
                                      <button data-testid={'delete-start-reply-' + reply.id} onClick={function() { handleDeleteReply(thread.id, reply.id); }} className="ml-auto p-1 hover:bg-red-50 rounded text-stone-400 hover:text-red-500 transition-colors" title="Delete reply">
                                        <Trash2 className="w-3.5 h-3.5" />
                                      </button>
                                    )}
                                  </div>
                                  <p className="text-stone-700 text-sm whitespace-pre-wrap">{reply.content}</p>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* Reply Form for Start Here */}
                        {activeStartThread === 'start-agreements' && (
                          <div className="mt-4 p-4 bg-stone-50 rounded-xl">
                            <p className="text-sm font-medium text-stone-700 mb-3">👋 Introduce yourself:</p>
                            <textarea 
                              value={newThreadContent} 
                              onChange={function(e) { setNewThreadContent(e.target.value); }} 
                              placeholder="1 - Name + where you're joining from&#10;2 - What made you join this Lab now&#10;3 - The area you want alignment most&#10;4 - One sentence you're practicing this month"
                              className="w-full p-4 rounded-xl border border-stone-200 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 text-sm" 
                              rows={5} 
                            />
                            <div className="flex justify-end mt-3">
                              <Button 
                                onClick={async function() {
                                  if (!newThreadContent.trim()) return;
                                  try {
                                    await axios.post(API_URL + '/community/threads/alignment-lab/' + thread.id + '/reply', {
                                      content: newThreadContent
                                    }, { headers: getAuthHeader() });
                                    toast.success('Welcome to the Lab! 🎉');
                                    setNewThreadContent('');
                                    // Refresh threads
                                    const response = await axios.get(API_URL + '/alignment-lab/start-here', { headers: getAuthHeader() });
                                    setStartHereThreads(response.data);
                                  } catch (error) {
                                    toast.error('Failed to post introduction');
                                  }
                                }} 
                                disabled={!newThreadContent.trim()}
                                className="bg-emerald-500 hover:bg-emerald-600"
                              >
                                <Send className="w-4 h-4 mr-2" />Post Introduction
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      )}

      {/* Week Content */}
      {activeSection === 'week' && isWeekLocked && (
        <div className="card-cosmic p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-stone-100 flex items-center justify-center">
            <Lock className="w-8 h-8 text-stone-400" />
          </div>
          <h2 className="text-xl font-bold text-stone-900 mb-2">Week {activeWeek} Coming Soon</h2>
          <p className="text-stone-600 mb-4">
            This content unlocks in <span className="font-bold text-violet-600">{getDaysUntilUnlock(activeWeek)} days</span>
          </p>
          <p className="text-sm text-stone-500">
            Focus on Week {unlockedWeeks} for now. Trust the process—each week builds on the last.
          </p>
        </div>
      )}

      {activeSection === 'week' && !isWeekLocked && currentWeek && (
        <div className="space-y-6">
          {/* Week Header */}
          <div className="card-cosmic p-6">
            <h2 className="text-xl font-bold text-stone-900 mb-2">Week {currentWeek.week}: {currentWeek.theme}</h2>
            
            {/* Lab Agreements - Show on all weeks */}
            <div className="mt-4 p-5 bg-emerald-50 rounded-xl border-2 border-emerald-200">
              <div className="flex items-center gap-2 text-emerald-600 text-sm font-medium mb-3">
                <span>📌</span> Lab Agreements
              </div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
                  <span className="text-white font-medium">H</span>
                </div>
                <div>
                  <p className="font-medium text-stone-900">Heather Stewart</p>
                </div>
              </div>
              <div className="text-stone-700 text-lg whitespace-pre-wrap">
                <p><strong>Ask first:</strong> Witness or Advice?</p>
                <p><strong>Share experience, not prescriptions:</strong> "What I tried…"</p>
                <p><strong>No rescuing, no diagnosing.</strong></p>
              </div>
            </div>
          </div>

          {/* MONDAY - Alignment Experiment */}
          <div className="card-cosmic p-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 rounded-xl bg-violet-500 flex items-center justify-center flex-shrink-0">
                <Play className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <div className="text-xs font-bold text-violet-600 mb-1">MONDAY • ALIGNMENT EXPERIMENT</div>
                <h3 className="font-bold text-stone-900 text-lg">{currentWeek.monday_experiment && currentWeek.monday_experiment.title}</h3>
              </div>
            </div>
            
            {/* Experiment Content */}
            <div className="bg-violet-50 rounded-xl p-5 mb-4">
              <div className="text-stone-700 whitespace-pre-wrap" dangerouslySetInnerHTML={{
                __html: (currentWeek.monday_experiment && currentWeek.monday_experiment.description || '')
                  .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
                  .replace(/^• /gm, '&bull; ')
              }} />
            </div>

            {/* Response Section for Experiment */}
            <div className="border-t border-stone-200 pt-4">
              <h4 className="font-medium text-stone-900 mb-3 flex items-center gap-2">
                <MessageCircle className="w-4 h-4 text-violet-500" />
                Share Your Response
              </h4>
              <textarea 
                value={mondayResponse}
                onChange={function(e) { setMondayResponse(e.target.value); }}
                placeholder={currentWeek.monday_experiment && currentWeek.monday_experiment.prompts 
                  ? currentWeek.monday_experiment.prompts.map(function(p, i) { return (i + 1) + ') ' + p; }).join('\n')
                  : "1) What 15-minute rest are you choosing?\n2) When will you do it? (be specific)"}
                className="w-full p-4 rounded-xl border border-stone-200 focus:border-violet-400 focus:ring-2 focus:ring-violet-100 text-sm" 
                rows={3}
                data-testid="monday-experiment-response"
              />
              <div className="flex justify-end mt-3">
                <Button 
                  onClick={function() { handlePostWeeklyThread('monday', mondayResponse, setMondayResponse); }}
                  disabled={!mondayResponse.trim() || posting}
                  className="bg-violet-500 hover:bg-violet-600"
                >
                  {posting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Post Response
                </Button>
              </div>
              
              {/* Responses will appear here */}
              <div className="mt-4 space-y-3">
                {threads.filter(function(t) { return t.thread_type === 'monday'; }).map(function(thread) {
                  return (
                    <div key={thread.id} className="p-3 bg-stone-50 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center">
                          <span className="text-violet-600 font-medium text-sm">{thread.author_name ? thread.author_name.charAt(0).toUpperCase() : '?'}</span>
                        </div>
                        <span className="font-medium text-stone-900 text-sm">{thread.author_name}</span>
                        {thread.author_is_alumni && (
                          <span className="px-1.5 py-0.5 text-xs font-medium bg-gradient-to-r from-amber-100 to-orange-100 text-amber-700 rounded-full border border-amber-200 flex items-center gap-1">
                            <GraduationCap className="w-2.5 h-2.5" /> Alumni
                          </span>
                        )}
                        <span className="text-xs text-stone-500">{new Date(thread.created_at).toLocaleDateString()}</span>
                        {(thread.author_id === user?.id || user?.is_admin) && (
                          <button onClick={function() { handleDeleteThread(thread.id); }} className="ml-auto p-1 hover:bg-red-50 rounded text-stone-400 hover:text-red-500 transition-colors" title="Delete">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                      <p className="text-stone-700 text-sm whitespace-pre-wrap">{thread.content}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* WEDNESDAY - Check-In */}
          {isWednesdayUnlocked(activeWeek) ? (
          <div className="card-cosmic p-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 rounded-xl bg-fuchsia-500 flex items-center justify-center flex-shrink-0">
                <Calendar className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <div className="text-xs font-bold text-fuchsia-600 mb-1">WEDNESDAY • CHECK-IN</div>
                <h3 className="font-bold text-stone-900 text-lg">
                  {currentWeek.wednesday_checkin && currentWeek.wednesday_checkin.title 
                    ? currentWeek.wednesday_checkin.title 
                    : 'Check-In: Wins + Wobbles + Data'}
                </h3>
              </div>
            </div>
            
            {/* Check-in Format */}
            <div className="bg-fuchsia-50 rounded-xl p-5 mb-4">
              {currentWeek.wednesday_checkin && currentWeek.wednesday_checkin.description ? (
                <div className="text-stone-700 whitespace-pre-wrap" dangerouslySetInnerHTML={{
                  __html: currentWeek.wednesday_checkin.description
                    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
                }} />
              ) : currentWeek.wednesday_checkin && currentWeek.wednesday_checkin.prompts ? (
                <div>
                  <ol className="text-stone-700 space-y-1 list-decimal list-inside">
                    {currentWeek.wednesday_checkin.prompts.map(function(p, i) { 
                      return <li key={i}>{p}</li>; 
                    })}
                  </ol>
                  {currentWeek.wednesday_checkin.witness_advice_note && (
                    <p className="text-stone-700 mt-3 pt-3 border-t border-fuchsia-200 font-medium">
                      {currentWeek.wednesday_checkin.witness_advice_note}
                    </p>
                  )}
                </div>
              ) : (
                <ol className="text-stone-700 space-y-1 list-decimal list-inside">
                  <li>My experiment: (what I did)</li>
                  <li>What I felt: (guilt, relief, resistance, calm, etc.)</li>
                  <li>What happened: (actual facts)</li>
                  <li>My tweak: (what I'll adjust next)</li>
                </ol>
              )}
            </div>

            {/* Live Meeting Link */}
            <div className="bg-white border-2 border-fuchsia-200 rounded-xl p-4 mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-fuchsia-100 flex items-center justify-center">
                  <Video className="w-5 h-5 text-fuchsia-600" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-stone-900">Live Check-In Call</p>
                  <p className="text-sm text-stone-500">Wednesdays at 6:00 PM ET</p>
                </div>
                <a 
                  href="https://us02web.zoom.us/j/84345759049?pwd=TPvk9WXOEHLWHywOEY1ibZxl9bmRBM.1" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-4 py-2 rounded-lg bg-fuchsia-500 hover:bg-fuchsia-600 text-white font-medium transition-colors"
                >
                  <Video className="w-4 h-4 mr-2" />Join Live
                </a>
              </div>
            </div>

            {/* Replay Section */}
            <div className="border-t border-stone-200 pt-4 mb-4">
              <h4 className="font-medium text-stone-900 mb-3 flex items-center gap-2">
                <Play className="w-4 h-4 text-fuchsia-500" />
                Replay
              </h4>
              <div className="bg-stone-50 rounded-xl p-4 text-center">
                <p className="text-stone-500 text-sm">Replay will be posted here after the live call</p>
              </div>
            </div>

            {/* Comments Section */}
            <div className="border-t border-stone-200 pt-4">
              <h4 className="font-medium text-stone-900 mb-3 flex items-center gap-2">
                <MessageCircle className="w-4 h-4 text-fuchsia-500" />
                Comments & Chat
              </h4>
              <textarea 
                value={wednesdayComment}
                onChange={function(e) { setWednesdayComment(e.target.value); }}
                placeholder={currentWeek.wednesday_checkin && currentWeek.wednesday_checkin.prompts 
                  ? currentWeek.wednesday_checkin.prompts.map(function(p, i) { return (i + 1) + ' - ' + p; }).join('\n')
                  : "1 - My experiment: (what I did)\n2 - What I felt: (guilt, relief, resistance, calm, etc.)\n3 - What happened: (actual facts)\n4 - My tweak: (what I'll adjust next)"}
                className="w-full p-4 rounded-xl border border-stone-200 focus:border-fuchsia-400 focus:ring-2 focus:ring-fuchsia-100 text-sm" 
                rows={5}
                data-testid="wednesday-checkin-comment"
              />
              <div className="flex justify-end mt-3">
                <Button 
                  onClick={function() { handlePostWeeklyThread('wednesday', wednesdayComment, setWednesdayComment); }}
                  disabled={!wednesdayComment.trim() || posting}
                  className="bg-fuchsia-500 hover:bg-fuchsia-600"
                >
                  {posting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Post Comment
                </Button>
              </div>
              
              {/* Comments will appear here */}
              <div className="mt-4 space-y-3">
                {threads.filter(function(t) { return t.thread_type === 'wednesday'; }).map(function(thread) {
                  return (
                    <div key={thread.id} className="p-3 bg-stone-50 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 rounded-full bg-fuchsia-100 flex items-center justify-center">
                          <span className="text-fuchsia-600 font-medium text-sm">{thread.author_name ? thread.author_name.charAt(0).toUpperCase() : '?'}</span>
                        </div>
                        <span className="font-medium text-stone-900 text-sm">{thread.author_name}</span>
                        {thread.author_is_alumni && (
                          <span className="px-1.5 py-0.5 text-xs font-medium bg-gradient-to-r from-amber-100 to-orange-100 text-amber-700 rounded-full border border-amber-200 flex items-center gap-1">
                            <GraduationCap className="w-2.5 h-2.5" /> Alumni
                          </span>
                        )}
                        <span className="text-xs text-stone-500">{new Date(thread.created_at).toLocaleDateString()}</span>
                        {(thread.author_id === user?.id || user?.is_admin) && (
                          <button onClick={function() { handleDeleteThread(thread.id); }} className="ml-auto p-1 hover:bg-red-50 rounded text-stone-400 hover:text-red-500 transition-colors" title="Delete">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                      <p className="text-stone-700 text-sm whitespace-pre-wrap">{thread.content}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          ) : (
          <div className="card-cosmic p-6 opacity-60">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-stone-300 flex items-center justify-center flex-shrink-0">
                <Lock className="w-6 h-6 text-stone-500" />
              </div>
              <div className="flex-1">
                <div className="text-xs font-bold text-stone-500 mb-1">WEDNESDAY • CHECK-IN</div>
                <h3 className="font-bold text-stone-500 text-lg">
                  {currentWeek.wednesday_checkin && currentWeek.wednesday_checkin.title 
                    ? currentWeek.wednesday_checkin.title 
                    : 'Check-In: Wins + Wobbles + Data'}
                </h3>
                <p className="text-sm text-stone-400 mt-1">Unlocks on Wednesday</p>
              </div>
            </div>
          </div>
          )}

          {/* FRIDAY - Community Thread */}
          {isFridayUnlocked(activeWeek) ? (
          <div className="card-cosmic p-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 rounded-xl bg-amber-500 flex items-center justify-center flex-shrink-0">
                <MessageCircle className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <div className="text-xs font-bold text-amber-600 mb-1">FRIDAY • COMMUNITY THREAD</div>
                <h3 className="font-bold text-stone-900 text-lg">
                  {currentWeek.friday_thread && currentWeek.friday_thread.title 
                    ? currentWeek.friday_thread.title 
                    : 'Guilt → Data (No Shame Allowed)'}
                </h3>
              </div>
            </div>
            
            {/* Thread Prompts */}
            <div className="bg-amber-50 rounded-xl p-5 mb-4">
              {currentWeek.friday_thread && currentWeek.friday_thread.description ? (
                <div className="text-stone-700 whitespace-pre-wrap" dangerouslySetInnerHTML={{
                  __html: currentWeek.friday_thread.description
                    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
                }} />
              ) : (
                <div>
                  <p className="text-stone-700 mb-3"><strong>If you felt guilt this week, share here as data</strong> — the more you share the clearer the picture of how to tweak for something new.</p>
                  <ul className="text-stone-700 space-y-1">
                    <li>• I rested when…</li>
                    <li>• The guilt story said…</li>
                    <li>• The truth was…</li>
                    <li>• What I learned about my worth is…</li>
                    <li>• Next week I want to try…</li>
                  </ul>
                </div>
              )}
            </div>

            {/* Comment Section */}
            <div className="border-t border-stone-200 pt-4">
              <h4 className="font-medium text-stone-900 mb-3 flex items-center gap-2">
                <MessageCircle className="w-4 h-4 text-amber-500" />
                Share Your Reflection
              </h4>
              <textarea 
                value={fridayReflection}
                onChange={function(e) { setFridayReflection(e.target.value); }}
                placeholder={currentWeek.friday_thread && currentWeek.friday_thread.prompts 
                  ? currentWeek.friday_thread.prompts.join('\n') 
                  : "I rested when…\nThe guilt story said…\nThe truth was…\nWhat I learned about my worth is…\nNext week I want to try…"}
                className="w-full p-4 rounded-xl border border-stone-200 focus:border-amber-400 focus:ring-2 focus:ring-amber-100 text-sm" 
                rows={5}
                data-testid="friday-thread-comment"
              />
              <div className="flex justify-end mt-3">
                <Button 
                  onClick={function() { handlePostWeeklyThread('friday', fridayReflection, setFridayReflection); }}
                  disabled={!fridayReflection.trim() || posting}
                  className="bg-amber-500 hover:bg-amber-600"
                >
                  {posting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Post Reflection
                </Button>
              </div>
              
              {/* Reflections will appear here */}
              <div className="mt-4 space-y-3">
                {threads.filter(function(t) { return t.thread_type === 'friday'; }).map(function(thread) {
                  return (
                    <div key={thread.id} className="p-3 bg-stone-50 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                          <span className="text-amber-600 font-medium text-sm">{thread.author_name ? thread.author_name.charAt(0).toUpperCase() : '?'}</span>
                        </div>
                        <span className="font-medium text-stone-900 text-sm">{thread.author_name}</span>
                        {thread.author_is_alumni && (
                          <span className="px-1.5 py-0.5 text-xs font-medium bg-gradient-to-r from-amber-100 to-orange-100 text-amber-700 rounded-full border border-amber-200 flex items-center gap-1">
                            <GraduationCap className="w-2.5 h-2.5" /> Alumni
                          </span>
                        )}
                        <span className="text-xs text-stone-500">{new Date(thread.created_at).toLocaleDateString()}</span>
                        {(thread.author_id === user?.id || user?.is_admin) && (
                          <button onClick={function() { handleDeleteThread(thread.id); }} className="ml-auto p-1 hover:bg-red-50 rounded text-stone-400 hover:text-red-500 transition-colors" title="Delete">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                      <p className="text-stone-700 text-sm whitespace-pre-wrap">{thread.content}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          ) : (
          <div className="card-cosmic p-6 opacity-60">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-stone-300 flex items-center justify-center flex-shrink-0">
                <Lock className="w-6 h-6 text-stone-500" />
              </div>
              <div className="flex-1">
                <div className="text-xs font-bold text-stone-500 mb-1">FRIDAY • COMMUNITY THREAD</div>
                <h3 className="font-bold text-stone-500 text-lg">
                  {currentWeek.friday_thread && currentWeek.friday_thread.title 
                    ? currentWeek.friday_thread.title 
                    : 'Guilt → Data (No Shame Allowed)'}
                </h3>
                <p className="text-sm text-stone-400 mt-1">Unlocks on Friday</p>
              </div>
            </div>
          </div>
          )}
        </div>
      )}

      {/* Alumni Lounge Section */}
      {activeSection === 'alumni' && alumniLoungeAccess && alumniContent && (
        <div className="space-y-6">
          {/* Welcome Message */}
          <div className="card-cosmic p-6 bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center flex-shrink-0">
                <GraduationCap className="w-7 h-7 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-stone-900 mb-2">Alumni Lounge</h2>
                <p className="text-stone-700 whitespace-pre-line">{alumniContent.welcome_message}</p>
              </div>
            </div>
          </div>

          {/* How to Help - Pinned Post */}
          <div className="card-cosmic p-6 bg-stone-50 border-2 border-stone-200">
            <div className="flex items-center gap-2 text-amber-600 text-sm font-medium mb-3">
              <span>📌</span> Pinned Guidance
            </div>
            <h3 className="font-bold text-stone-900 text-lg mb-3">{alumniContent.how_to_help.title}</h3>
            <div className="text-stone-700 whitespace-pre-wrap" dangerouslySetInnerHTML={{
              __html: alumniContent.how_to_help.content
                .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
            }} />
          </div>

          {/* Thread Categories */}
          <div className="card-cosmic p-6">
            <h3 className="font-bold text-stone-900 mb-4">Conversation Threads</h3>
            <div className="space-y-3">
              {alumniContent.threads && alumniContent.threads.map(function(threadInfo) {
                var Icon = alumniThreadIcons[threadInfo.id] || Star;
                var isActive = activeAlumniThread === threadInfo.id;
                
                return (
                  <button
                    key={threadInfo.id}
                    onClick={function() { setActiveAlumniThread(isActive ? null : threadInfo.id); }}
                    className={'w-full p-4 rounded-xl text-left transition-all ' + 
                      (isActive 
                        ? 'bg-amber-50 border-2 border-amber-300' 
                        : 'bg-stone-50 border border-stone-200 hover:border-amber-300'
                      )}
                  >
                    <div className="flex items-center gap-4">
                      <div className={'w-12 h-12 rounded-xl flex items-center justify-center ' + 
                        (isActive ? 'bg-amber-500' : 'bg-amber-100')}>
                        <Icon className={'w-6 h-6 ' + (isActive ? 'text-white' : 'text-amber-600')} />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-bold text-stone-900">{threadInfo.title}</h4>
                        <p className="text-sm text-amber-600 italic">{threadInfo.subtitle}</p>
                        <p className="text-sm text-stone-600">{threadInfo.description}</p>
                      </div>
                      <ChevronRight className={'w-5 h-5 transition-transform ' + 
                        (isActive ? 'rotate-90 text-amber-500' : 'text-stone-400')} />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Thread Posts Area */}
          {activeAlumniThread && (
            <div className="card-cosmic p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  {(function() {
                    var Icon = alumniThreadIcons[activeAlumniThread] || Star;
                    return <Icon className="w-6 h-6 text-amber-500" />;
                  })()}
                  <h3 className="font-bold text-stone-900 text-lg">
                    {alumniContent.threads.find(function(t) { return t.id === activeAlumniThread; })?.title}
                  </h3>
                </div>
                <Button 
                  onClick={function() { setShowThreadForm(!showThreadForm); }} 
                  variant={showThreadForm ? 'outline' : 'default'}
                  className={showThreadForm ? '' : 'bg-amber-500 hover:bg-amber-600'}
                >
                  {showThreadForm ? 'Cancel' : 'Share'}
                </Button>
              </div>

              {/* Post Form */}
              {showThreadForm && (
                <div className="mb-6 p-4 bg-amber-50 rounded-xl">
                  <textarea 
                    value={newThreadContent} 
                    onChange={function(e) { setNewThreadContent(e.target.value); }} 
                    placeholder={activeAlumniThread === 'alumni-worth-standard' 
                      ? 'I used to... now I...' 
                      : activeAlumniThread === 'alumni-scripts' 
                        ? 'The exact sentence I used this week...'
                        : 'I held the line when...'}
                    className="w-full p-4 rounded-xl border border-amber-200 focus:border-amber-400 focus:ring-2 focus:ring-amber-100" 
                    rows={4} 
                  />
                  <div className="flex justify-end mt-3">
                    <Button 
                      onClick={async function() {
                        if (!newThreadContent.trim()) return;
                        try {
                          var threadTitle = alumniContent.threads.find(function(t) { return t.id === activeAlumniThread; })?.title || 'Share';
                          await axios.post(API_URL + '/alignment-lab/alumni-lounge/threads', {
                            category: activeAlumniThread.replace('alumni-', ''),
                            title: threadTitle,
                            content: newThreadContent
                          }, { headers: getAuthHeader() });
                          toast.success('Shared! 🎉');
                          setNewThreadContent('');
                          setShowThreadForm(false);
                          // Refresh threads
                          var response = await axios.get(API_URL + '/alignment-lab/alumni-lounge/threads', { headers: getAuthHeader() });
                          setAlumniThreads(response.data);
                        } catch (error) {
                          toast.error('Failed to post');
                        }
                      }} 
                      disabled={!newThreadContent.trim()}
                      className="bg-amber-500 hover:bg-amber-600"
                    >
                      <Send className="w-4 h-4 mr-2" />Share
                    </Button>
                  </div>
                </div>
              )}

              {/* Thread Posts */}
              {alumniThreads.filter(function(t) { 
                return t.thread_category === activeAlumniThread.replace('alumni-', ''); 
              }).length === 0 ? (
                <p className="text-center text-stone-500 py-8">Be the first to share in this thread!</p>
              ) : (
                <div className="space-y-4">
                  {alumniThreads.filter(function(t) { 
                    return t.thread_category === activeAlumniThread.replace('alumni-', ''); 
                  }).map(function(thread) {
                    return (
                      <div key={thread.id} className="p-4 bg-stone-50 rounded-xl">
                        <div className="flex items-center gap-3 mb-3">
                          {thread.author_avatar ? (
                            <img src={thread.author_avatar} alt="" className="w-10 h-10 rounded-full object-cover" />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                              <span className="text-amber-600 font-medium">{thread.author_name ? thread.author_name.charAt(0).toUpperCase() : '?'}</span>
                            </div>
                          )}
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-stone-900">{thread.author_name}</p>
                              {thread.author_is_alumni && (
                                <span className="px-2 py-0.5 text-xs font-medium bg-gradient-to-r from-amber-100 to-orange-100 text-amber-700 rounded-full border border-amber-200 flex items-center gap-1">
                                  <GraduationCap className="w-3 h-3" /> Alumni
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-stone-500">{new Date(thread.created_at).toLocaleDateString()}</p>
                          </div>
                        </div>
                        <p className="text-stone-700 whitespace-pre-wrap">{thread.content}</p>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AlignmentLabPage;
